export const DAY_MS = 24 * 60 * 60 * 1000;

export const startOfDay = (date: Date): Date =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const addDays = (date: Date, days: number): Date => {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
};

export const daysBetween = (from: Date, to: Date): number =>
	Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);

export const parseDate = (value: string | null): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const clamp = (value: number, min: number, max: number): number =>
	Math.min(Math.max(value, min), max);

export interface DayRange {
	from: Date | undefined;
	to?: Date | undefined;
}

/** The end hour that means "through the whole of the last day". */
export const END_OF_DAY = 24;

/**
 * A campaign or content window as the merchant thinks of it: calendar days, and
 * the hour it starts and ends on those days, in the shop's time zone.
 *
 * `range` holds local Date objects only because the day picker wants them; they
 * carry a calendar day, never an instant.
 */
export interface Schedule {
	range: DayRange | undefined;
	startHour: number;
	endHour: number;
}

export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

interface WallClock {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

const wallClock = (instant: Date, timeZone: string): WallClock => {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hourCycle: "h23",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}).formatToParts(instant);
	const part = (type: Intl.DateTimeFormatPartTypes) =>
		Number(parts.find((entry) => entry.type === type)?.value ?? 0);
	return {
		year: part("year"),
		month: part("month"),
		day: part("day"),
		hour: part("hour") % 24,
		minute: part("minute"),
		second: part("second"),
	};
};

const offsetAt = (instant: number, timeZone: string): number => {
	const clock = wallClock(new Date(instant), timeZone);
	const asUtc = Date.UTC(
		clock.year,
		clock.month - 1,
		clock.day,
		clock.hour,
		clock.minute,
		clock.second,
	);
	return asUtc - Math.floor(instant / 1000) * 1000;
};

/**
 * The instant a wall-clock time in `timeZone` names. The offset is read at a
 * first guess and again at the result, so a time either side of a daylight
 * saving change lands on the correct side of it. Day overflow is allowed, which
 * is how "midnight after the 7th" is asked for as the 8th at 0:00.
 */
const zonedInstant = (day: Date, hour: number, timeZone: string): number => {
	const guess = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), hour);
	const first = guess - offsetAt(guess, timeZone);
	return guess - offsetAt(first, timeZone);
};

/** The calendar day an instant falls on in `timeZone`, as a picker-ready local Date. */
export const zonedDay = (value: string | null, timeZone: string): Date | null => {
	const instant = parseDate(value);
	if (!instant) return null;
	const clock = wallClock(instant, timeZone);
	return new Date(clock.year, clock.month - 1, clock.day);
};

/**
 * An end on the stroke of midnight is the end of the previous day, which is
 * how windows saved before hours existed (23:59:59.999) read back too.
 */
export const toSchedule = (
	startsAt: string | null,
	endsAt: string | null,
	timeZone: string,
): Schedule => {
	const start = parseDate(startsAt);
	const end = parseDate(endsAt);
	if (!start) return { range: undefined, startHour: 0, endHour: END_OF_DAY };

	const startClock = wallClock(start, timeZone);
	const from = new Date(startClock.year, startClock.month - 1, startClock.day);
	if (!end) return { range: { from }, startHour: startClock.hour, endHour: END_OF_DAY };

	const endClock = wallClock(end, timeZone);
	const endDay = new Date(endClock.year, endClock.month - 1, endClock.day);
	if (endClock.hour === 23 && endClock.minute === 59) {
		return { range: { from, to: endDay }, startHour: startClock.hour, endHour: END_OF_DAY };
	}
	if (endClock.hour === 0 && endClock.minute === 0) {
		return {
			range: { from, to: addDays(endDay, -1) },
			startHour: startClock.hour,
			endHour: END_OF_DAY,
		};
	}
	return { range: { from, to: endDay }, startHour: startClock.hour, endHour: endClock.hour };
};

export const scheduleToIso = (schedule: Schedule, timeZone: string) => {
	const { range, startHour, endHour } = schedule;
	const endsAt = range?.to
		? endHour === END_OF_DAY
			? zonedInstant(addDays(range.to, 1), 0, timeZone) - 1
			: zonedInstant(range.to, endHour, timeZone)
		: null;
	return {
		startsAt: range?.from
			? new Date(zonedInstant(range.from, startHour, timeZone)).toISOString()
			: null,
		endsAt: endsAt === null ? null : new Date(endsAt).toISOString(),
	};
};

/** True when the window as picked would end at or before it starts. */
export const endsBeforeStart = (schedule: Schedule): boolean => {
	const { range, startHour, endHour } = schedule;
	if (!range?.from || !range.to) return false;
	return daysBetween(range.from, range.to) === 0 && endHour <= startHour;
};
